import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, FileTextIcon, LayersIcon, LockIcon, SearchIcon, UnlockIcon } from 'lucide-react';
import type { Project } from '../types/project';
import { BrandLogo } from './BrandLogo';
import { useTheme } from '../contexts/ThemeContext';
import { resolveBrand } from '../utils/brandStyle';
interface ProjectSelectorProps {
  projects: Project[];
  hasAccess: (projectId: string) => boolean;
  onSelect: (projectId: string) => void;
}
export function ProjectSelector({
  projects,
  hasAccess,
  onSelect
}: ProjectSelectorProps) {
  const {
    theme
  } = useTheme();
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter(project => [project.name, project.client, project.stage, project.owner].join(' ').toLowerCase().includes(term));
  }, [projects, query]);
  return <main className="mx-auto w-full max-w-6xl px-6 pb-24 pt-14" data-id="element-93" data-mp="3dlhj4ndjjz96">
      <div className="max-w-2xl" data-id="element-94" data-mp="3iku2uz948q7j">
        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-accent" data-id="element-95" data-mp="3il3s2kmc6tc6">
          Step 1 of 2
        </span>
        <h1 className="mt-4 font-display text-4xl leading-tight tracking-tight text-shell-text sm:text-5xl" data-id="element-96" data-mp="33n1oiafb27ux">
          Choose a project
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-shell-dim" data-id="element-97" data-mp="2uvj941q6nw2w">
          Every project has its own set of tool logins and shared documents — and its own
          password. Pick the one you&apos;re working in, enter its password, and you&apos;ll land
          on its access list.
        </p>
      </div>

      <div className="relative mt-9 max-w-sm" data-id="element-98" data-mp="2pvn72811qepz">
        <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-shell-faint" aria-hidden="true" data-id="element-99" data-mp="2pwg3af2dpltv" />
        <label htmlFor="project-search" className="sr-only" data-id="element-100" data-mp="2fwxnmati4m7m">
          Search projects
        </label>
        <input id="project-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search by project, client or owner" className="w-full rounded-xl border border-shell-border bg-shell-surface py-2.5 pl-10 pr-4 text-sm text-shell-text outline-none transition placeholder:text-shell-faint focus:border-accent focus:ring-2 focus:ring-accent" data-id="element-101" data-mp="mgbdgf389wcy" />
      </div>

      {filtered.length === 0 ? <div className="mt-10 rounded-2xl border border-dashed border-shell-border bg-shell-surface px-6 py-14 text-center" data-id="element-102" data-mp="10gchg3eshjwn">
          <p className="text-sm font-medium text-shell-text" data-id="element-103" data-mp="ve51l2qu9jp1">No projects match “{query}”</p>
          <p className="mt-1.5 text-xs text-shell-dim" data-id="element-104" data-mp="re37pfrchukb">
            Try a client name, or ask an admin to add you to the project.
          </p>
        </div> : <ul className="mt-8 grid gap-4 sm:grid-cols-2" data-id="element-105" data-mp="3c3ittxfmcv3m">
          {filtered.map((project, index) => {
        const open = hasAccess(project.id);
        const brand = resolveBrand(project.brand, theme);
        return <motion.li key={project.id} initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          delay: index * 0.05,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-106" data-mp="1voehpep2909g" data-mpi={index}>
                <button type="button" onClick={() => onSelect(project.id)} aria-label={open ? `Open ${project.name}` : `${project.name} — password required`} className="group flex h-full w-full flex-col overflow-hidden rounded-2xl border border-shell-border bg-shell-surface text-left transition hover:-translate-y-0.5 hover:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-107" data-mp="2abzi30ujwldo">
                  <span className="flex items-center justify-between gap-3 border-b px-6 py-5" style={{
              backgroundColor: brand.bg,
              borderColor: brand.border,
              borderBottomWidth: 1
            }} data-id="element-108" data-mp="tzu6le66r5z9">
                    <BrandLogo brand={brand} data-id="element-109" data-mp="8m0p6qyfio13" />
                    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em]" style={{
                borderColor: open ? brand.accent : brand.border,
                backgroundColor: open ? brand.tint : 'transparent',
                color: open ? brand.accent : brand.textDim
              }} data-id="element-110" data-mp="3n5x0yeknn4b">
                      {open ? <UnlockIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-111" data-mp="2tchlzbzt491l" /> : <LockIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-112" data-mp="2oepv8lawesyz" />}
                      {open ? 'Unlocked' : 'Locked'}
                    </span>
                  </span>

                  <span className="flex flex-1 flex-col p-6" data-id="element-113" data-mp="18nnntve7h0dm">
                    <span className="flex items-center gap-2" data-id="element-114" data-mp="txpwj6kzbuk7">
                      <span className="rounded-full border border-shell-border bg-shell-surface-alt px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-shell-dim" data-id="element-115" data-mp="tzj36lvlees7">
                        {project.stage}
                      </span>
                      <span className="font-mono text-[10px] text-shell-faint" data-id="element-116" data-mp="1rlzy5kgusgas">
                        Updated {project.updated}
                      </span>
                    </span>

                    <span className="mt-4 block font-display text-2xl leading-snug text-shell-text" data-id="element-117" data-mp="1hnahtw4lv3wn">
                      {project.name}
                    </span>
                    <span className="mt-1 block text-xs font-semibold text-accent" data-id="element-118" data-mp="258nyd0vc0xh">
                      {project.client}
                    </span>
                    <span className="mt-3 block flex-1 text-sm leading-relaxed text-shell-dim" data-id="element-119" data-mp="28vbwn2td18sw">
                      {project.summary}
                    </span>

                    <span className="mt-6 flex items-center gap-4 border-t border-shell-border pt-4 font-mono text-[10px] uppercase tracking-[0.14em] text-shell-faint" data-id="element-120" data-mp="23y20l4bgtyw3">
                      <span className="inline-flex items-center gap-1.5" data-id="element-121" data-mp="1u5d3xfkkqpkt">
                        <LayersIcon className="h-3 w-3" aria-hidden="true" data-id="element-122" data-mp="1q4krkdgeo4kb" />
                        {project.tools.length} tools
                      </span>
                      <span className="inline-flex items-center gap-1.5" data-id="element-123" data-mp="1l7kma4rdr5u9">
                        <FileTextIcon className="h-3 w-3" aria-hidden="true" data-id="element-124" data-mp="1g6dfo90z4cp6" />
                        {project.documents.length} docs
                      </span>
                      <ArrowRightIcon className="ml-auto h-4 w-4 text-accent transition group-hover:translate-x-0.5" aria-hidden="true" data-id="element-125" data-mp="3akedcqksetno" />
                    </span>
                  </span>
                </button>
              </motion.li>;
      })}
        </ul>}
    </main>;
}